/**
 * Shape Draw Preview
 * Shows a preview outline while the user is drawing a shape
 */

import React from 'react';
import type { ToolId } from '@/types/tools';
import { useToolbarStore } from '@/stores/toolbarStore';

interface ShapeDrawState {
  isDrawing: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  layerId: string | null;
}

interface ShapeDrawPreviewProps {
  drawState: ShapeDrawState;
  toolId: ToolId;
  zoom: number;
}

export function ShapeDrawPreview({ drawState, toolId, zoom }: ShapeDrawPreviewProps) {
  const shapeType = useToolbarStore((s) => s.shapeDefaults.shapeType);
  const { startX, startY, currentX, currentY } = drawState;

  // Get the canvas element position
  const canvas = document.getElementById('ad-canvas');
  if (!canvas) return null;

  const canvasRect = canvas.getBoundingClientRect();

  // Calculate preview bounds
  const x = Math.min(startX, currentX) * zoom + canvasRect.left;
  const y = Math.min(startY, currentY) * zoom + canvasRect.top;
  const width = Math.abs(currentX - startX) * zoom;
  const height = Math.abs(currentY - startY) * zoom;

  // For line tool, we need to draw a line, not a rectangle
  if (toolId === 'line') {
    const x1 = startX * zoom + canvasRect.left;
    const y1 = startY * zoom + canvasRect.top;
    const x2 = currentX * zoom + canvasRect.left;
    const y2 = currentY * zoom + canvasRect.top;

    return (
      <svg
        className="fixed pointer-events-none z-50"
        style={{
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
        }}
      >
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="var(--color-primary)"
          strokeWidth={2}
          strokeDasharray="4 4"
        />
        {/* Endpoint markers */}
        <circle cx={x1} cy={y1} r={4} fill="var(--color-primary)" />
        <circle cx={x2} cy={y2} r={4} fill="var(--color-primary)" />
      </svg>
    );
  }

  // For shapes - use shape type from store
  const isRound = shapeType === 'circle' || shapeType === 'ellipse';

  return (
    <div
      className="fixed pointer-events-none z-50 border-2 border-dashed border-primary"
      style={{
        left: x,
        top: y,
        width,
        height,
        borderRadius: isRound ? '50%' : undefined,
      }}
    />
  );
}
